import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import salesOrderLinesService from "../../../services/legacy/salesOrderLinesService";
import itemsService from "../../../services/legacy/itemsService";

const ROW_STYLE_ID = "sales-line-material-alternatives-row-style";

const cellStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap",
  minWidth: 0,
  width: "100%",
  minHeight: "46px",
};

const countStyle = {
  flex: "0 0 92px",
  lineHeight: 1.25,
};

const selectStyle = {
  flex: "1 1 160px",
  minWidth: "120px",
  maxWidth: "560px",
  height: "40px",
  lineHeight: "38px",
  padding: "0 10px",
  color: "#0f172a",
  backgroundColor: "#ffffff",
};

const actionButtonStyle = {
  flex: "1 1 92px",
  padding: "7px 12px",
  lineHeight: 1.1,
  minWidth: "72px",
  minHeight: "40px",
};

const formatAlternativeLabel = (alternative) => {
  if (!alternative) {
    return "";
  }
  const family = [
    alternative.typeItemId,
    alternative.gramajeItemId,
    alternative.vendItemId,
  ].filter(Boolean).join("/");
  const name = alternative.salesItemName || alternative.vendorDescription || alternative.itemName || "";
  const cert = alternative.certTypeId ? ` - ${alternative.certTypeId}` : "";
  return `${alternative.itemId}${family ? ` - ${family}` : ""}${name ? ` - ${name}` : ""}${cert}`;
};

const isSelectableAlternative = (alternative) => (
  alternative?.selectable !== false && !String(alternative?.itemId || "").toUpperCase().endsWith("XXXX")
);


const ensureRowStyle = () => {
  if (typeof document === "undefined" || document.getElementById(ROW_STYLE_ID)) {
    return;
  }
  const style = document.createElement("style");
  style.id = ROW_STYLE_ID;
  style.textContent = `
    tr:has(.sales-line-material-alternatives-cell) {
      background-color: #dcfce7;
    }
    tr:has(.sales-line-material-alternatives-cell) td {
      background-color: inherit;
    }
  `;
  document.head.appendChild(style);
};

export default function SalesLineMaterialAlternativesCell({ row, suppressRowCss = false, onChanged }) {
  const { t } = useTranslation();
  const alternatives = Array.isArray(row?.materialAlternatives) ? row.materialAlternatives : [];
  const displayedAlternatives = alternatives.filter((alternative) => !alternative.currentItem);
  const defaultAlternative = displayedAlternatives.find(isSelectableAlternative) || displayedAlternatives[0];
  const [selectedItemId, setSelectedItemId] = useState(defaultAlternative?.itemId || "");
  const [busyAction, setBusyAction] = useState("");
  const sourceIsPlaceholder = String(row?.itemId || "").trim().toUpperCase().endsWith("XXXX");

  useEffect(() => {
    if (!suppressRowCss) {
      ensureRowStyle();
    }
  }, [suppressRowCss]);

  useEffect(() => {
    if (!displayedAlternatives.some((alternative) => alternative.itemId === selectedItemId)) {
      setSelectedItemId(defaultAlternative?.itemId || "");
    }
  }, [defaultAlternative?.itemId, displayedAlternatives, selectedItemId]);

  const selectedAlternative = useMemo(
    () => displayedAlternatives.find((alternative) => alternative.itemId === selectedItemId),
    [displayedAlternatives, selectedItemId],
  );
  const selectedIsSelectable = isSelectableAlternative(selectedAlternative);
  const selectedIsPlaceholder = String(selectedAlternative?.itemId || "").trim().toUpperCase().endsWith("XXXX");

  if (displayedAlternatives.length === 0) {
    return "";
  }

  const runAction = async (action) => {
    if (!selectedItemId || !selectedIsSelectable || (action === "change" && sourceIsPlaceholder) || busyAction) {
      return;
    }

    const messageKey = action === "change"
      ? "salesLines.materialAlternatives.confirmChange"
      : "salesLines.materialAlternatives.confirmAddLine";
    if (!window.confirm(t(messageKey, { itemId: selectedItemId }))) {
      return;
    }

    setBusyAction(action);
    try {
      if (action === "change") {
        await salesOrderLinesService.changeMaterial(row.id || row, selectedItemId);
      } else {
        await salesOrderLinesService.addMaterialLine(row.id || row, selectedItemId);
      }
      if (onChanged) {
        onChanged();
      }
    } catch (error) {
      console.error("Error applying sales line material alternative:", error);
      window.alert(t("salesLines.materialAlternatives.actionError"));
    } finally {
      setBusyAction("");
    }
  };

  const createItem = async () => {
    if (busyAction) return;
    setBusyAction("create");
    try {
      await itemsService.requestCreation({
        dataAreaId: row?.id?.dataAreaId || row?.dataAreaId,
        typeItemId: selectedAlternative?.typeItemId,
        gramajeItemId: selectedAlternative?.gramajeItemId,
        vendItemId: selectedAlternative?.vendItemId,
        width: selectedAlternative?.width ?? row?.width,
        depth: selectedAlternative?.depth ?? row?.depth,
        iSalesId: row?.id?.iSalesId || row?.iSalesId,
      });
      window.alert(t("salesLines.materialAlternatives.createQueued"));
    } catch (error) {
      console.error("Error queueing item creation:", error);
      window.alert(t("salesLines.materialAlternatives.createError"));
    } finally {
      setBusyAction("");
    }
  };

  return (
    <div
      className={suppressRowCss ? undefined : "sales-line-material-alternatives-cell"}
      style={cellStyle}
      title={selectedAlternative ? formatAlternativeLabel(selectedAlternative) : ""}
    >
      <span style={countStyle}>{t("salesLines.materialAlternatives.count", { count: displayedAlternatives.length })}</span>
      <select
        value={selectedItemId}
        onChange={(event) => setSelectedItemId(event.target.value)}
        style={{
          ...selectStyle,
          color: selectedIsSelectable ? selectStyle.color : "#b91c1c",
          borderColor: selectedIsSelectable ? undefined : "#ef4444",
        }}
        disabled={!!busyAction}
      >
        {displayedAlternatives.map((alternative) => (
          <option
            key={alternative.itemId}
            value={alternative.itemId}
            style={{ color: isSelectableAlternative(alternative) ? "#0f172a" : "#b91c1c" }}
          >
            {formatAlternativeLabel(alternative)}
          </option>
        ))}
      </select>
      {selectedIsPlaceholder ? (
        <button type="button" className="btn btn-default btn-sm" style={actionButtonStyle}
          disabled={!!busyAction} onClick={createItem}>
          {busyAction === "create" ? t("common.loading") : t("salesLines.materialAlternatives.createItem")}
        </button>
      ) : null}
      {!selectedIsPlaceholder ? <>
      <button
        type="button"
        className="btn btn-default btn-sm"
        style={actionButtonStyle}
        disabled={!selectedItemId || !selectedIsSelectable || sourceIsPlaceholder || !!busyAction}
        onClick={() => runAction("change")}
      >
        {busyAction === "change" ? t("common.loading") : t("salesLines.materialAlternatives.changeMaterial")}
      </button>
      <button
        type="button"
        className="btn btn-default btn-sm"
        style={actionButtonStyle}
        disabled={!selectedItemId || !selectedIsSelectable || !!busyAction}
        onClick={() => runAction("add")}
      >
        {busyAction === "add" ? t("common.loading") : t("salesLines.materialAlternatives.addLine")}
      </button>
      </> : null}
    </div>
  );
}

